import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  defaults
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { theme } from '../../tailwind.config';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { playerGameweekPrediction, playerName } from '@lib/player-functions';
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

defaults.font.family = 'Poppins';

const SuggestedTransferChart = ({
  playersIn,
  playersOut,
  playerInColor,
  playerOutColor,
  variant
}) => {
  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  const isWildcardOrFreehit = playersOut?.length > 2;
  const POINTS_DECIMAL_PLACES = 1;
  const MAX_GAMEWEEK = 38;

  if (nextGameweek == null) return null;

  const getPointsTotalWithDecimal = (
    aggregate: number,
    points: number,
    decimalPlaces: number
  ) => {
    return (
      (Number(aggregate.toFixed(decimalPlaces)) * 100 +
        Number(points.toFixed(decimalPlaces)) * 100) /
      100
    );
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true, // Add this line
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12,
            family: 'Open Sans'
          },
          color: theme.colors.neutral['600']
        }
      },
      y: {
        beginAtZero: true,
        grace: 1,
        grid: {
          //@ts-ignore
          borderDash: [4, 5],
          lineWidth: 1,
          color: theme.colors.neutral[300]
        },
        type: 'linear',
        display: true,
        ticks: {
          stepSize: 2,
          font: {
            size: 12,
            family: 'Open Sans'
          },
          color: theme.colors.neutral['600']
        }
      }
    },
    plugins: {
      legend: {
        align: 'start',
        position: variant === 'mini' ? 'bottom' : 'top',
        display: true,
        labels: {
          boxHeight: 8,
          usePointStyle: true,
          font: {
            size: variant === 'mini' ? 10 : 16,
            family: 'Open Sans'
          },
          generateLabels: (chart) => {
            //@ts-ignore
            const originalLabels =
              ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            return originalLabels.map((label) => ({
              ...label,
              text: ` ${label.text}`
            }));
          }
        }
      },
      tooltip: {
        padding: 8,
        titleFont: {
          family: 'Open Sans'
        },
        bodyFont: {
          family: 'Open Sans'
        },
        footerFont: {
          family: 'Open Sans'
        },
        backgroundColor: function (context) {
          return context?.tooltip?.labelColors?.[0]?.backgroundColor;
        },
        displayColors: false,
        callbacks: {
          label: ({ formattedValue }) => {
            return `${formattedValue} points`;
          }
        }
      }
    }
  };

  const plugins = [
    {
      id: 'shadedBackground',
      beforeDraw: (chart) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const yAxis = chart.scales.y;
        ctx.save();
        xAxis.ticks.forEach((tick, index) => {
          const x = xAxis.getPixelForTick(index);

          ctx.fillStyle = theme.colors['neutral'][100];

          // Calculate the dimensions of the rectangle
          const width = (xAxis.width / xAxis.ticks.length) * 0.8;
          const height = yAxis.bottom - yAxis.top;
          const radius = 10; // Adjust this value to change the border radius

          // Draw the rounded rectangle
          ctx.beginPath();
          ctx.moveTo(x - width / 2 + radius, yAxis.top);
          ctx.lineTo(x + width / 2 - radius, yAxis.top);
          ctx.arcTo(
            x + width / 2,
            yAxis.top,
            x + width / 2,
            yAxis.top + radius,
            radius
          );
          ctx.lineTo(x + width / 2, yAxis.top + height - radius);
          ctx.arcTo(
            x + width / 2,
            yAxis.bottom,
            x + width / 2 - radius,
            yAxis.bottom,
            radius
          );
          ctx.lineTo(x - width / 2 + radius, yAxis.bottom);
          ctx.arcTo(
            x - width / 2,
            yAxis.bottom,
            x - width / 2,
            yAxis.bottom - radius,
            radius
          );
          ctx.lineTo(x - width / 2, yAxis.top + radius);
          ctx.arcTo(
            x - width / 2,
            yAxis.top,
            x - width / 2 + radius,
            yAxis.top,
            radius
          );
          ctx.closePath();
          ctx.fill();
        });

        ctx.restore();
      }
    }
  ];

  const gameweeks = [
    nextGameweek,
    nextGameweek + 1,
    nextGameweek + 2,
    nextGameweek + 3,
    nextGameweek + 4
  ].filter((gw) => gw <= MAX_GAMEWEEK);

  const labels = gameweeks && gameweeks.map((gw) => `GW${gw}`);

  let playersInNames = '';
  const playerInPoints = [0, 0, 0, 0, 0];
  playersIn.forEach((player, i) => {
    gameweeks.forEach((gw, index) => {
      const points = playerGameweekPrediction(player, gw);
      playerInPoints[index] = getPointsTotalWithDecimal(
        playerInPoints[index],
        points,
        POINTS_DECIMAL_PLACES
      );
    });
    if (isWildcardOrFreehit) {
      playersInNames = 'Recommended transfers';
      return;
    }
    if (i > 0) {
      playersInNames += ' + ';
    }
    playersInNames += playerName(player);
  });

  let playersOutNames = '';
  const playerOutPoints = [0, 0, 0, 0, 0];
  playersOut.forEach((player, i) => {
    gameweeks.forEach((gw, index) => {
      const points = playerGameweekPrediction(player, gw);
      playerOutPoints[index] = getPointsTotalWithDecimal(
        playerOutPoints[index],
        points,
        POINTS_DECIMAL_PLACES
      );
    });
    if (isWildcardOrFreehit) {
      playersOutNames = 'Current players';
      return;
    }
    if (i > 0) {
      playersOutNames += ' + ';
    }
    playersOutNames += playerName(player);
  });

  const data: ChartData<'line'> = {
    labels: labels,
    datasets: [
      {
        label: playersOutNames,
        data: playerOutPoints,
        borderColor: playerOutColor,
        backgroundColor: playerOutColor,
        tension: 0.5,
        borderWidth: 5,
        pointHitRadius: 10
      },
      {
        label: playersInNames,
        data: playerInPoints,
        borderColor: playerInColor,
        backgroundColor: playerInColor,
        tension: 0.5,
        borderWidth: 5,
        pointHitRadius: 10
      }
    ]
  };

  return (
    <>
      <Line options={options} data={data} plugins={plugins} />
    </>
  );
};

export default SuggestedTransferChart;
